import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { ScoreGauge } from "@/components/ScoreGauge";
import { DASHBOARD } from "@/constants/testIds";
import { toast } from "sonner";
import { ArrowUpRight, Sparkles, Compass, User, ShieldCheck, FileText, KanbanSquare, CalendarDays, Bot, Terminal, Building2, Radar, Plug, ScrollText, KeyRound, GitBranch, Network, Brain, Globe2, Crown, FileBarChart2, Palette, Share2 } from "lucide-react";

const MODULE_ICONS = {
  life_strategy: Crown,
  trusted_intelligence: ShieldCheck,
  identity_core: User,
  career_intelligence: Sparkles,
  opportunity_engine: Compass,
  hidden_opportunity_engine: Radar,
  decision_engine: Compass,
  career_simulation: GitBranch,
  global_mobility: Globe2,
  knowledge_graph: Network,
  learning_memory: Brain,
  connector_engine: Plug,
  altiora_vault: ShieldCheck,
  document_generator: FileText,
  application_tracker: KanbanSquare,
  career_calendar: CalendarDays,
  ai_assistant: Bot,
  voice_assistant: Bot,
  multi_tenant: Building2,
  biometric_auth: Terminal,
  audit_log: ScrollText,
  mfa_admin: KeyRound,
  report_intelligence: FileBarChart2,
  white_label: Palette,
  enterprise_api: KeyRound,
  secure_share: Share2,
};

const MODULE_ROUTES = {
  life_strategy: "/app/life-strategy",
  trusted_intelligence: "/app/trust",
  identity_core: "/app/profile",
  career_intelligence: "/app/intelligence",
  opportunity_engine: "/app/opportunities",
  hidden_opportunity_engine: "/app/hidden-opportunities",
  decision_engine: "/app/decision",
  career_simulation: "/app/simulation",
  global_mobility: "/app/mobility",
  knowledge_graph: "/app/graph",
  learning_memory: "/app/memory",
  connector_engine: "/app/connectors",
  altiora_vault: "/app/vault",
  document_generator: "/app/documents",
  application_tracker: "/app/tracker",
  career_calendar: "/app/calendar",
  ai_assistant: "/app/assistant",
  report_intelligence: "/app/reports",
  white_label: "/app/white-label",
  enterprise_api: "/app/enterprise-api",
  secure_share: "/app/reports",
};

export default function ModulesGrid() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [score, setScore] = useState(null);
  const [modules, setModules] = useState([]);
  const [oppCount, setOppCount] = useState({ visible: 0, hidden: 0 });
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [diagLoading, setDiagLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, m, o, p] = await Promise.all([
          api.get("/intelligence/score"),
          api.get("/admin/modules"),
          api.get("/opportunities?limit=100"),
          api.get("/profile"),
        ]);
        setScore(s.data);
        setModules(m.data.items || []);
        const items = o.data.items || [];
        setOppCount({
          visible: items.filter((i) => i.kind === "visible").length,
          hidden: items.filter((i) => i.kind === "hidden").length,
        });
        setProfileCompletion(p.data.diagnostic_score || 0);
      } catch (e) {
        // silent
      }
    })();
  }, []);

  const runDiagnostic = async () => {
    setDiagLoading(true);
    try {
      const { data } = await api.post("/profile/diagnostic");
      setProfileCompletion(data.diagnostic_score || profileCompletion);
      toast.success(lang === "fr" ? "Diagnostic mis à jour" : "Diagnostic updated");
    } catch {
      toast.error(lang === "fr" ? "Erreur du diagnostic" : "Diagnostic error");
    } finally { setDiagLoading(false); }
  };

  return (
    <div data-testid={DASHBOARD.root} className="min-h-full">
      <TopBar
        title={`${t("dashboard.welcome")}, ${user?.name?.split(" ")[0] || ""}`}
        subtitle={user?.email}
      />
      <div className="px-6 md:px-10 py-8 md:py-10 space-y-8">

        {/* Metric row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div data-testid={DASHBOARD.scoreCard}
               className="col-span-1 md:col-span-1 rounded-md border border-[#E4E1D8] bg-white p-6 flex flex-col items-center">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62] mb-4">
              {t("dashboard.score_label")}
            </div>
            <ScoreGauge score={score?.score || 0} band={score?.band || "—"} />
            <Link to="/app/intelligence" className="mt-6 inline-flex items-center gap-1 text-xs text-[#0B2545] hover:text-[#DCC29A]">
              Career Intelligence <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>

          <div className="rounded-md border border-[#E4E1D8] bg-white p-6 flex flex-col">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62]">
              {t("dashboard.opportunities_label")}
            </div>
            <div className="mt-4 flex items-baseline gap-4">
              <div>
                <div className="font-mono text-4xl text-[#1F2937] tabular-nums">{oppCount.visible}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mt-1">visible</div>
              </div>
              <div className="border-l border-[#E4E1D8] pl-4">
                <div className="font-mono text-4xl text-[#0B2545] tabular-nums">{oppCount.hidden}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#0B2545] mt-1">hidden</div>
              </div>
            </div>
            <Link to="/app/opportunities" className="mt-auto pt-6 inline-flex items-center gap-1 text-xs text-[#0B2545] hover:text-[#DCC29A]">
              Opportunity Engine <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>

          <div className="rounded-md border border-[#E4E1D8] bg-white p-6 flex flex-col">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62]">
              {t("dashboard.profile_completion")}
            </div>
            <div className="mt-4 flex items-baseline">
              <div className="font-mono text-4xl text-[#1F2937] tabular-nums">{profileCompletion}</div>
              <div className="ml-1 text-[#5B5B62] font-mono">/100</div>
            </div>
            <div className="mt-3 h-1.5 bg-[#EBE8DE] rounded-full overflow-hidden">
              <div className="h-full bg-[#0B2545]" style={{ width: `${profileCompletion}%` }} />
            </div>
            <button
              data-testid={DASHBOARD.runDiagnostic}
              onClick={runDiagnostic}
              disabled={diagLoading}
              className="mt-auto pt-6 inline-flex items-center gap-1 text-xs text-[#0B2545] hover:text-[#DCC29A] disabled:opacity-50"
            >
              {diagLoading ? t("common.loading") : t("dashboard.run_diagnostic")}
              <ArrowUpRight className="h-3 w-3" />
            </button>
          </div>
        </div>

        {/* Modules grid */}
        <div>
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="font-display text-xl tracking-tight text-[#1F2937]">{t("dashboard.modules_title")}</h2>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
              {modules.length} · registered
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {modules.map((flag) => {
              const Icon = MODULE_ICONS[flag.key] || Sparkles;
              const route = MODULE_ROUTES[flag.key];
              const enabled = flag.enabled_for_me;
              const label = flag.meta?.[`label_${lang}`] || flag.key;
              const desc = flag.meta?.[`description_${lang}`];
              const status = flag.status;
              return (
                <Link
                  key={flag.key}
                  to={route || "#"}
                  data-testid={DASHBOARD.moduleCard}
                  className={`group rounded-md border p-4 transition-all ${
                    enabled
                      ? "border-[#E4E1D8] bg-white hover:border-[#B8B5AC] hover:bg-[#EBE8DE]"
                      : "border-[#E4E1D8]/60 bg-white/50 opacity-70 pointer-events-none"
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <Icon className="h-5 w-5 text-[#0B2545]" strokeWidth={1.5} />
                    <StatusPill status={status} lang={lang} />
                  </div>
                  <div className="text-sm font-medium text-[#1F2937]">{label}</div>
                  {desc && <div className="mt-1 text-xs text-[#5B5B62] line-clamp-2">{desc}</div>}
                  <div className="mt-3 font-mono text-[9px] uppercase tracking-widest text-[#5B5B62]">
                    {flag.key}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, lang }) {
  const map = {
    active: { fr: "Actif", en: "Active", cls: "bg-[#2E8B57]/15 text-[#2E8B57] border-[#2E8B57]/30" },
    beta: { fr: "Bêta", en: "Beta", cls: "bg-[#4682B4]/15 text-[#4682B4] border-[#4682B4]/30" },
    planned: { fr: "Prévu", en: "Planned", cls: "bg-[#0B2545]/8 text-[#0B2545] border-[#0B2545]/25" },
    premium: { fr: "Premium", en: "Premium", cls: "bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30" },
    founder_only: { fr: "Fondateur", en: "Founder", cls: "bg-[#8B0000]/10 text-[#D97706] border-[#D4AF37]/30" },
  };
  const meta = map[status] || map.planned;
  return (
    <span className={`inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border ${meta.cls}`}>
      {meta[lang] || meta.fr}
    </span>
  );
}
